import { useState, useCallback } from 'react';

import { get, isEmpty } from 'ravenjs/utils/lodash';
import { formatDate } from 'ravenjs/utils/date';

import { CLIENT_SOLUTION_REPORT } from 'constants/reports';

export function useClientSolutionReport({
    companyPartnerId,
    isLoggedInPartnerUser,
    fetchPartners,
    fetchClientSolutionReport,
    fetchTodayClientSolutionReport,
    onFetchTodayClientSolutionReport,
}) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [reportData, setReportData] = useState([]);
    const [noReportFound, setNoReportFound] = useState(false);

    const fetchGeneratedReport = async (selectedPartnerId, selectedStartDate, selectedEndDate) => {
        try {
            setNoReportFound(false);
            setLoading(true);
            let partnerId = selectedPartnerId;

            if (isLoggedInPartnerUser) {
                partnerId = companyPartnerId;
            }

            let startDate = selectedStartDate;
            let endDate = selectedEndDate;
            startDate = new Date(startDate).getTime();
            endDate = new Date(endDate).getTime();
            startDate = formatDate(startDate, 'YYYY-MM-DD');
            endDate = formatDate(endDate, 'YYYY-MM-DD');

            const response = await fetchClientSolutionReport({
                startDate,
                endDate,
                partnerId,
            });
            const updatedResponse = isEmpty(response) ? [] : response;
            setReportData(updatedResponse);
            if (!updatedResponse?.length) {
                setNoReportFound(true);
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setError('');
            setLoading(false);
        }
    };

    const onClearSearch = useCallback(() => {
        setReportData([]);
    }, []);

    const onClickTodaySnapshotButton = async selectedPartnerId => {
        try {
            setLoading(true);
            let partnerId = selectedPartnerId;

            if (isLoggedInPartnerUser) {
                partnerId = companyPartnerId;
            }

            await fetchTodayClientSolutionReport({
                partnerId,
            });
            onFetchTodayClientSolutionReport();
        } catch (e) {
            setError(e.message);
        } finally {
            setError('');
            setLoading(false);
        }
    };

    const fetchPartnersData = async searchSpec => {
        try {
            const response = await fetchPartners({
                limit: 25,
                isActive: true,
                searchSpec,
                withLicenses: true,
            });
            const partners = get(response, 'companies', []);

            // GN-24932 : Filtering out the intuit partner company from the drop down list. This is a dirty check which will be removed later.
            return partners
                .filter(
                    partner => partner.displayName !== CLIENT_SOLUTION_REPORT.INTUIT_PARTNER_NAME
                )
                .map(({ displayName, partnerId, licenses }) => ({
                    label: `${displayName}`,
                    value: partnerId,
                    hasLicense: !!licenses?.length,
                }));
        } catch (e) {
            setError(e.message);
            return [];
        } finally {
            setError('');
        }
    };

    function getReportData(sortOrder = 'asc') {
        const sortedReportData = [...reportData];
        if (sortOrder === 'asc') {
            sortedReportData.sort((a, b) => a.createdAt - b.createdAt);
        } else {
            sortedReportData.sort((a, b) => b.createdAt - a.createdAt);
        }
        return sortedReportData?.map(({ createdAt, downloadUrl }) => {
            const generationDate = `${
                CLIENT_SOLUTION_REPORT.REPORT_LIST.REPORT_NAME_SUFFIX
            } ${formatDate(new Date(createdAt * 1000), 'MM/DD/YYYY')}`;

            return {
                generationDate,
                downloadUrl,
            };
        });
    }

    return {
        error,
        loading,
        noReportFound,
        fetchGeneratedReport,
        fetchPartnersData,
        getReportData,
        onClearSearch,
        onClickTodaySnapshotButton,
    };
}
